import { Controller } from "@hotwired/stimulus";

/**
 * Steps through purchase-complete onboarding panels (questions + upsell reminder).
 * Does not reuse survey_controller — that one only knows questions + an end panel.
 */
export default class extends Controller<HTMLElement> {
  static values = {
    currentStep: { type: Number, default: 0 },
    exitPath: String,
  };

  static targets = ["panel", "stepper", "stepLabel"];

  declare panelTargets: HTMLElement[];
  declare stepperTargets: HTMLElement[];
  declare stepLabelTargets: HTMLElement[];
  declare hasStepperTarget: boolean;
  declare currentStepValue: number;
  declare exitPathValue: string;

  private readonly hiddenClass = "tw-hidden";
  private initialRender = true;

  connect() {
    this.showCurrentStep();
    this.initialRender = false;
  }

  currentStepValueChanged() {
    this.showCurrentStep();
  }

  next() {
    if (this.currentStepValue >= this.panelTargets.length - 1) return;

    this.currentStepValue += 1;
  }

  back() {
    if (this.currentStepValue <= 0) return;

    this.currentStepValue -= 1;
  }

  finish() {
    if (!this.exitPathValue) return;

    window.location.assign(this.exitPathValue);
  }

  private showCurrentStep() {
    const step = Number(this.currentStepValue);

    this.panelTargets.forEach((panel, index) => {
      this.setHidden(panel, index !== step);
    });

    this.syncButtonActivateForCurrentPanel();
    this.updateStepper();

    if (!this.initialRender) {
      this.scrollActivePanelIntoView();
    }
  }

  private setHidden(element: HTMLElement, hidden: boolean) {
    element.classList.toggle(this.hiddenClass, hidden);
  }

  private syncButtonActivateForCurrentPanel() {
    const panel = this.panelTargets[this.currentStepValue];
    if (!panel) return;

    panel
      .querySelectorAll<HTMLElement>('[data-controller~="button-activate"]')
      .forEach((form) => {
        form.dispatchEvent(
          new CustomEvent("button-activate:sync", { bubbles: false })
        );
      });
  }

  private updateStepper() {
    if (!this.hasStepperTarget) return;

    const panel = this.panelTargets[this.currentStepValue];
    if (!panel) return;

    const isUpsell = this.isUpsellPanel(panel);
    const questionNumber = this.questionNumberForStep(this.currentStepValue);

    this.stepperTargets.forEach((stepper) => {
      this.setHidden(stepper, isUpsell);
    });

    if (isUpsell) return;

    this.stepLabelTargets.forEach((label) => {
      label.textContent = String(questionNumber);
    });

    this.element
      .querySelectorAll("[data-onboarding-survey-step]")
      .forEach((bar) => {
        const step = Number((bar as HTMLElement).dataset.onboardingSurveyStep);
        bar.classList.toggle("tw-bg-tango", step <= questionNumber);
        bar.classList.toggle("tw-bg-neutral-200", step > questionNumber);
      });
  }

  private questionNumberForStep(stepIndex: number): number {
    return this.panelTargets
      .slice(0, stepIndex + 1)
      .filter((panel) => !this.isUpsellPanel(panel)).length;
  }

  private isUpsellPanel(panel: HTMLElement): boolean {
    return panel.dataset.onboardingSurveyStepKind === "upsell";
  }

  private scrollActivePanelIntoView() {
    const panel = this.panelTargets[this.currentStepValue];
    if (!panel) return;

    requestAnimationFrame(() => {
      panel.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
}
